/**
 * VoiceFAB — production-ready floating action button for the onboarding voice assistant.
 * Uses Lucide-style inline SVG icons with color + animation per voice state.
 * Replaces the RobotAvatar PNG in the onboarding context.
 */

import type { VoiceStatus } from '@/redux/slices/onboardingVoiceSlice'

interface VoiceFABProps {
  status: VoiceStatus
  onClick?: () => void
  title?: string
  size?: 'sm' | 'md' | 'lg'
}

const STATE_BG: Record<VoiceStatus, string> = {
  idle:       '#4E38B5',
  connecting: '#7c3aed',
  listening:  '#059669',
  thinking:   '#d97706',
  speaking:   '#0284c7',
  error:      '#CF4148',
}

const STATE_SHADOW: Record<VoiceStatus, string> = {
  idle:       '0 4px 14px rgba(78,56,181,0.45)',
  connecting: '0 4px 14px rgba(124,58,237,0.45)',
  listening:  '0 4px 18px rgba(5,150,105,0.5)',
  thinking:   '0 4px 14px rgba(217,119,6,0.45)',
  speaking:   '0 4px 18px rgba(2,132,199,0.5)',
  error:      '0 4px 14px rgba(207,65,72,0.45)',
}

const SIZE_CLASS = {
  sm: 'w-12 h-12',
  md: 'w-14 h-14',
  lg: 'w-16 h-16',
}

const ICON_SIZE = {
  sm: 20,
  md: 22,
  lg: 26,
}

/** Mic icon (idle / listening) */
function MicIcon({ size, color }: { size: number; color: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
      <path d="M19 10v2a7 7 0 01-14 0v-2M12 19v4M8 23h8" />
    </svg>
  )
}

/** MicOff icon (error) */
function MicOffIcon({ size, color }: { size: number; color: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="1" y1="1" x2="23" y2="23" />
      <path d="M9 9v3a3 3 0 005.12 2.12M15 9.34V4a3 3 0 00-5.94-.6M17 16.95A7 7 0 015 12v-2m14 0v2a7 7 0 01-.11 1.23M12 19v4M8 23h8" />
    </svg>
  )
}

/** AudioLines / waveform icon (speaking) */
function AudioLinesIcon({ size, color }: { size: number; color: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round">
      <line x1="2"  y1="12" x2="2"  y2="12" />
      <line x1="6"  y1="8"  x2="6"  y2="16" />
      <line x1="10" y1="5"  x2="10" y2="19" />
      <line x1="14" y1="8"  x2="14" y2="16" />
      <line x1="18" y1="10" x2="18" y2="14" />
      <line x1="22" y1="12" x2="22" y2="12" />
    </svg>
  )
}

/** Spinner icon (connecting / thinking) */
function SpinnerIcon({ size, color }: { size: number; color: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" className="animate-spin">
      <path d="M21 12a9 9 0 11-6.219-8.56" />
    </svg>
  )
}

export function VoiceFAB({ status, onClick, title, size = 'md' }: VoiceFABProps) {
  const bg = STATE_BG[status]
  const shadow = STATE_SHADOW[status]
  const iconSize = ICON_SIZE[size]
  const isActive = status !== 'idle' && status !== 'error'

  const icon = (() => {
    switch (status) {
      case 'listening':
        return <MicIcon size={iconSize} color="white" />
      case 'speaking':
        return <AudioLinesIcon size={iconSize} color="white" />
      case 'connecting':
      case 'thinking':
        return <SpinnerIcon size={iconSize} color="white" />
      case 'error':
        return <MicOffIcon size={iconSize} color="white" />
      default:
        return <MicIcon size={iconSize} color="white" />
    }
  })()

  return (
    <div className="relative flex-shrink-0">
      {/* Expanding ring on listening / speaking */}
      {(status === 'listening' || status === 'speaking') && (
        <span
          className="absolute inset-0 rounded-full animate-ping opacity-30 pointer-events-none"
          style={{ backgroundColor: bg }}
        />
      )}

      <button
        onClick={onClick}
        title={title ?? (isActive ? 'Stop Voice Mode' : 'Start Voice Mode')}
        aria-label={title ?? (isActive ? 'Stop Voice Mode' : 'Start Voice Mode')}
        className={`${SIZE_CLASS[size]} rounded-full flex items-center justify-center transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-400 hover:scale-105 active:scale-95`}
        style={{ backgroundColor: bg, boxShadow: shadow }}
      >
        {icon}
      </button>
    </div>
  )
}
